import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchChamber, fetchChamberSession, fetchCourse, fetchProgress, saveChamberStage, submitChamber, loc } from "@/lib/physicsApi";
import { EmeraldCosmos, PhysicsImage } from "./PhysicsImage";
import { useSEO } from "@/lib/useSEO";
import { useT } from "@/i18n/LanguageContext";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";

function Stage({ stage, initial, onSave, lang }) {
  const L = (v) => loc(v, lang);
  const [resp, setResp] = useState(initial || {});
  const [saved, setSaved] = useState(!!initial);
  const set = (k, v) => { setResp((r) => ({ ...r, [k]: v })); setSaved(false); };
  const fieldsDone = (stage.fields || []).every((f) => (resp[f.id] || "").trim().length >= 25);
  const itemsDone = (stage.items || []).every((_, i) => resp[String(i)]);
  const certaintyDone = !stage.certainty || (resp.certainty && (resp.certaintyWhy || "").trim().length >= 15);
  const complete = (stage.kind === "classification" ? itemsDone && (!stage.why_field || (resp.why || "").trim().length >= 25) : fieldsDone) && certaintyDone;

  return (
    <section className="border border-[#c8a95a]/20 bg-[#14211a] p-6 sm:p-8" data-testid={`physics-chamber-stage-${stage.id}`}>
      <h2 className="font-serif text-xl text-bone">{L(stage.title)}</h2>
      <p className="mt-3 text-sm text-fog font-reading leading-[1.85]">{L(stage.instruction)}</p>
      <div className="mt-6 space-y-5">
        {stage.kind === "classification" && (stage.items || []).map((it, i) => (
          <div key={i} className="border border-white/10 p-4">
            <p className="text-sm text-bone/90 font-reading mb-3">{L(it.text)}</p>
            <div className="flex flex-wrap gap-2">
              {stage.options.map((opt) => (
                <button key={opt} type="button" onClick={() => set(String(i), opt)}
                  data-testid={`physics-chamber-${stage.id}-item${i}-${opt.replace(/\s+/g, "-")}`}
                  aria-pressed={resp[String(i)] === opt}
                  className={`px-3 py-2 min-h-[40px] text-[10px] tracking-[0.15em] uppercase border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 ${resp[String(i)] === opt ? "bg-[#2e5040] text-[#eaf5ee] border-[#7fb69a]/60" : "border-white/15 text-fog/70 hover:border-[#7fb69a]/50"}`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
        {stage.kind === "classification" && stage.why_field && (
          <div>
            <label htmlFor={`why-${stage.id}`} className="block text-sm text-bone/90 font-reading mb-2">
              {lang === "nl" ? "Benoem in eigen woorden wat deze sortering zichtbaar maakt, inclusief je eigen aandeel." : "Name in your own words what this sorting reveals, including your own share."}
            </label>
            <textarea id={`why-${stage.id}`} rows={3} value={resp.why || ""} data-testid={`physics-chamber-${stage.id}-why`}
              onChange={(e) => set("why", e.target.value)}
              className="w-full bg-transparent border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
          </div>
        )}
        {(stage.fields || []).map((f) => (
          <div key={f.id}>
            <label htmlFor={`st-${stage.id}-${f.id}`} className="block text-sm text-bone/90 font-reading mb-2">{L(f.label)}</label>
            <textarea id={`st-${stage.id}-${f.id}`} rows={3} value={resp[f.id] || ""} data-testid={`physics-chamber-${stage.id}-${f.id}`}
              onChange={(e) => set(f.id, e.target.value)}
              className="w-full bg-transparent border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
          </div>
        ))}
        {stage.certainty && (
          <div className="border-t border-white/10 pt-5">
            <p className="text-sm text-bone/90 font-reading mb-3">{lang === "nl" ? "Hoe zeker ben je van dit besluit?" : "How certain are you of this decision?"}</p>
            <div className="flex gap-2 mb-3">
              {["low", "medium", "high"].map((c) => (
                <button key={c} type="button" onClick={() => set("certainty", c)} aria-pressed={resp.certainty === c}
                  data-testid={`physics-chamber-certainty-${c}`}
                  className={`px-4 py-2 min-h-[40px] text-[10px] tracking-[0.2em] uppercase border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 ${resp.certainty === c ? "bg-[#2e5040] text-[#eaf5ee] border-[#7fb69a]/60" : "border-white/15 text-fog/70"}`}>{c}</button>
              ))}
            </div>
            <textarea rows={2} value={resp.certaintyWhy || ""} data-testid="physics-chamber-certainty-why"
              onChange={(e) => set("certaintyWhy", e.target.value)}
              placeholder={lang === "nl" ? "Waarom dit niveau van zekerheid?" : "Why this level of certainty?"}
              className="w-full bg-transparent border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
          </div>
        )}
      </div>
      <div className="mt-6 flex items-center gap-4">
        <button onClick={async () => { await onSave(resp); setSaved(true); }} disabled={!complete}
          data-testid={`physics-chamber-save-${stage.id}`}
          className="px-6 py-3 min-h-[44px] bg-[#2e5040] text-[#eaf5ee] text-[10px] tracking-[0.22em] uppercase hover:brightness-125 active:brightness-90 disabled:opacity-35 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7fb69a]">
          {saved ? (lang === "nl" ? "Bewaard ✓" : "Saved ✓") : (lang === "nl" ? "Deze fase vastleggen" : "Set down this stage")}
        </button>
        {!complete && <span className="text-[11px] text-fog/50">{lang === "nl" ? "De Kamer leest alleen volledige antwoorden." : "The Chamber reads only complete answers."}</span>}
      </div>
    </section>
  );
}

export default function ChamberOfNatureFate() {
  const { lang } = useT();
  const L = (v) => loc(v, lang);
  const [chamber, setChamber] = useState(null);
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState([]);
  const [session, setSession] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useSEO({ title: "The Chamber of Nature & Fate | Stoic Physics | StoicOs.ai Academy", canonical: "https://stoicos.ai/academy/physics/chamber" });

  useEffect(() => {
    fetchChamber().then(setChamber).catch(() => {});
    fetchCourse().then(setCourse).catch(() => {});
    fetchProgress().then((d) => setProgress(d.records || []));
    fetchChamberSession().then((d) => {
      setSession(d.session);
      if (d.session?.evaluation) setEvaluation(d.session.evaluation);
    }).catch(() => {});
  }, []);

  if (!chamber || !course) return <div className="min-h-screen bg-ink" />;
  const media = course.media || {};
  const allSlugs = course.phases.flatMap((p) => p.lessons.map((l) => l.slug));
  const walked = allSlugs.filter((s) => progress.some((r) => r.lessonSlug === s)).length;
  const gateOpen = walked >= allSlugs.length;
  const stagesSaved = session?.stages ? Object.keys(session.stages).length : 0;
  const allStagesSaved = chamber.stages.every((s) => session?.stages?.[s.id]);

  const doSubmit = async () => {
    setSubmitting(true); setError("");
    try {
      const { evaluation: ev } = await submitChamber();
      setEvaluation(ev);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(typeof e.detail === "object" ? (lang === "nl" ? "Nog niet alle fasen zijn vastgelegd." : "Not all stages have been set down yet.") : e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <main className="min-h-screen bg-ink px-6 sm:px-10 py-24" data-testid="physics-chamber-page">
      <div className="max-w-3xl mx-auto space-y-12">
        <header>
          <DisciplineSwitcher active="physics" />
          <nav aria-label="Breadcrumb" className="text-[10px] tracking-[0.25em] uppercase text-[#7fb69a] mb-8">
            <Link to="/academy/physics" className="hover:text-gold">Stoic Physics</Link> / <span className="text-fog">Chamber of Nature & Fate</span>
          </nav>
          <div className="flex items-center gap-4"><EmeraldCosmos size={40} /><h1 className="font-serif text-3xl sm:text-4xl text-bone">The Chamber of Nature & Fate</h1></div>
          <div className="mt-8 max-w-xl"><PhysicsImage media={media} assetKey="physics-14-chamber-of-nature-and-fate" alt="The Chamber of Nature & Fate" /></div>
        </header>

        {evaluation ? (
          <section data-testid="physics-chamber-evaluation" className="space-y-8">
            <div className="border border-gold/40 bg-[#f3e9d2]/[0.05] p-8 text-center">
              <p className="text-[10px] tracking-[0.35em] uppercase text-gold mb-3">{lang === "nl" ? "Erkenning" : "Recognition"}</p>
              <h2 className="font-serif text-3xl text-bone">{L(chamber.recognitionTitle)}</h2>
              <p className="mt-4 text-sm text-fog font-reading leading-[1.9] max-w-xl mx-auto">{L(chamber.recognitionText)}</p>
            </div>
            <div className="space-y-3">
              {evaluation.rubric.map((r) => (
                <div key={r.id} className="border border-[#c8a95a]/15 bg-[#14211a] p-5" data-testid={`physics-rubric-${r.id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-bone/90 font-reading">{r.label}</span>
                    <span className={`text-[10px] tracking-[0.15em] uppercase whitespace-nowrap ${r.level === "Demonstrated" ? "text-gold" : r.level === "Practised" ? "text-[#7fb69a]" : "text-fog/55"}`}>{r.level}</span>
                  </div>
                  {r.reading && <p className="mt-2 text-[13px] text-fog/80 font-reading leading-relaxed">{r.reading}</p>}
                </div>
              ))}
            </div>
            <div className="border-l-2 border-gold/50 pl-6 py-2">
              <p className="text-sm text-fog font-reading leading-[1.95] whitespace-pre-line" data-testid="physics-chamber-closing">{evaluation.closing}</p>
            </div>
            <p className="text-sm text-fog/70 font-reading">{L(chamber.closing)}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/academy/physics/path" className="px-6 py-3 border border-[#7fb69a]/50 text-[#bfdccb] text-[11px] tracking-[0.2em] uppercase hover:bg-[#1e332a]" data-testid="physics-chamber-to-path">Path of Nature</Link>
              <Link to="/academy" className="px-6 py-3 border border-white/15 text-fog/80 text-[11px] tracking-[0.2em] uppercase hover:border-gold/40">{lang === "nl" ? "Terug naar de Academy" : "Return to the Academy"}</Link>
            </div>
          </section>
        ) : !gateOpen ? (
          <section className="border border-[#c8a95a]/25 bg-[#14211a] p-8" data-testid="physics-chamber-gate">
            <p className="font-serif text-xl text-bone">{lang === "nl" ? "De deuren zijn nog gesloten." : "The doors are still closed."}</p>
            <p className="mt-3 text-sm text-fog font-reading leading-[1.85]">
              {lang === "nl"
                ? `De Kamer beoordeelt de hele beweging: waarnemen, begrijpen, plaatsen, aanvaarden, meebewegen, bijdragen. Loop eerst alle achttien lessen. Je hebt er ${walked} van de ${allSlugs.length} betreden.`
                : `The Chamber examines the whole movement: observe, understand, place, accept, move with, contribute. Walk all eighteen lessons first. You have entered ${walked} of ${allSlugs.length}.`}
            </p>
            <Link to="/academy/physics" className="inline-block mt-6 px-6 py-3 bg-[#2e5040] text-[#eaf5ee] text-[11px] tracking-[0.22em] uppercase hover:brightness-125">{lang === "nl" ? "Naar de leergang" : "To the course"}</Link>
          </section>
        ) : (
          <>
            <section>
              <h2 className="font-serif text-2xl text-gold">{L(chamber.title)}</h2>
              <p className="mt-4 text-fog font-reading leading-[1.95]" data-testid="physics-chamber-intro">{L(chamber.intro)}</p>
              <div className="mt-8 space-y-4">
                {chamber.testimonies.map((tst, i) => (
                  <blockquote key={i} className="border-l-2 border-[#7fb69a]/50 pl-5 py-1">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#7fb69a]">{L(tst.source)}</p>
                    <p className="mt-1.5 text-sm text-fog/90 font-reading leading-[1.85]">{L(tst.text)}</p>
                  </blockquote>
                ))}
              </div>
              <p className="mt-6 text-sm text-bone/80 font-reading italic leading-[1.85]">{L(chamber.pressure)}</p>
            </section>

            <div className="text-xs tracking-[0.2em] uppercase text-[#7fb69a]" data-testid="physics-chamber-progress">{stagesSaved} / {chamber.stages.length} {lang === "nl" ? "fasen vastgelegd" : "stages set down"}</div>

            {chamber.stages.map((s) => (
              <Stage key={s.id} stage={s} lang={lang} initial={session?.stages?.[s.id]}
                onSave={async (resp) => {
                  await saveChamberStage(s.id, resp);
                  setSession((prev) => ({ ...(prev || {}), stages: { ...(prev?.stages || {}), [s.id]: resp } }));
                }} />
            ))}

            <section className="border border-gold/30 bg-[#f3e9d2]/[0.04] p-8 text-center">
              <p className="text-sm text-fog font-reading mb-5">
                {lang === "nl" ? "Wanneer alle zeven fasen zijn vastgelegd, leest de examinator het geheel." : "When all seven stages are set down, the examiner reads the whole."}
              </p>
              <button onClick={doSubmit} disabled={!allStagesSaved || submitting} data-testid="physics-chamber-submit"
                className="px-10 py-4 min-h-[52px] bg-gold/90 text-ink text-xs tracking-[0.25em] uppercase hover:bg-gold active:brightness-90 disabled:opacity-35 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-gold">
                {submitting ? (lang === "nl" ? "De examinator leest…" : "The examiner is reading…") : (lang === "nl" ? "Leg voor aan de Kamer" : "Present to the Chamber")}
              </button>
              {error && <p className="mt-4 text-sm text-red-300/90" data-testid="physics-chamber-error">{error}</p>}
            </section>
          </>
        )}
      </div>
    </main>
  );
}
